import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Heart, MessageCircle, Share2, Send, MoreHorizontal, ShieldAlert, UserX, Trash2, Megaphone } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { ParallaxBackground } from './utils/parallax';
import { formatFirestoreTime } from './utils/timeFormat';
import { YapService, UserService } from '../services/api';
import { StorageService } from '../services/StorageService';
import { toast } from 'sonner';
import foodOfDayImage from '../assets/foodofweek.jpg';
import type { AfroEvent, FoodSection } from './LegionEventManagerModal';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { collection, doc, addDoc, updateDoc, arrayUnion, arrayRemove, onSnapshot, query, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { getDbSafe } from '../services/firebaseConfig';
import { useVoiceRecorder } from '../hooks/useVoiceRecorder';
import { VoiceRecorderUI } from './VoiceRecorderUI';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { reportToWatchers } from '../services/WatcherService';
import { useMetricDelta } from './MetricDeltaContext';
import { LikeButton } from './ui/LikeButton';

interface EventDetailsScreenProps {
    onBack: () => void;
    event?: AfroEvent | null;
}

interface Reply {
    id: string;
    authorId: string;
    author: string;
    username: string;
    avatar: string;
    content: string;
    time: string;
    likes: number;
    audioUrl?: string;
    duration?: number;
    type?: 'text' | 'voice';
}

export const PILAU_SECTIONS: FoodSection[] = [
    {
        emoji: '🍽️',
        heading: 'About Pilau',
        content: "Pilau is a traditional spiced rice dish from the Swahili Coast, known for its deep aroma and layered flavors.",
    },
    {
        emoji: '🌿',
        heading: "How It's Made",
        content: '• Spices toasted\n• Onions sautéed\n• Rice simmered in broth',
    }
];

export const WHAT_IS_AFROYAP_SECTIONS: FoodSection[] = [
    {
        emoji: '🌍',
        heading: 'Our Mission',
        content: "AfroYap is the world's first platform designed specifically to connect the global Pan-African community. We're building a space for culture, commerce, and connection.",
    },
    {
        emoji: '✨',
        heading: 'Core Features',
        content: "• Create Tribes for your local community\n• Launch Forums for deep discussions\n• Use Gold to support creators and friends.",
    }
];

export const WHAT_IS_GOLD_SECTIONS: FoodSection[] = [
    {
        emoji: '💰',
        heading: 'The Gold Economy',
        content: "Gold is the currency of AfroYap. It powers everything from creating communities to making new friends.",
    },
    {
        emoji: '📊',
        heading: 'Current Costs',
        content: "• Create Forum: 500 Gold\n• Create Tribe: 1000 Gold\n• Add Friend: 10 Gold",
    },
    {
        emoji: '🚀',
        heading: 'How to Earn',
        content: "• Daily Logins: Maintain your streak!\n• Rewarded Ads: 10 Gold per view\n• Special Events: Keep an eye out!",
    }
];

const HEADING_STYLE = {
    textShadow: 'none',
    WebkitTextStroke: '0px',
    fontFamily: "'NarnialOne', 'Poppins', sans-serif",
    filter: 'none',
    fontWeight: '400',
} as const;

function EventSection({ section }: { section: FoodSection }) {
    const lines = section.content.split('\n');
    return (
        <div className="pt-4 first:pt-0">
            <h3 className="text-xl font-normal mb-4 text-black dark:text-white flex items-center" style={HEADING_STYLE}>
                <span className="font-sans mr-2 text-2xl">{section.emoji}</span>
                {section.heading}
            </h3>
            <div className="text-[#666666] dark:text-[#E5E5E5] space-y-3 leading-relaxed text-sm pl-0.5">
                {lines.map((line, i) => (
                    <p key={i} className="mb-2 last:mb-0">{line}</p>
                ))}
            </div>
        </div>
    );
}

export function EventDetailsScreen({ onBack, event }: EventDetailsScreenProps) {
    const { userProfile } = useUserData();
    const { filterVisible } = useContentFilter();
    const { adjustMetric, getOptimisticCount } = useMetricDelta();
    const [liked, setLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [commentText, setCommentText] = useState('');
    const [replies, setReplies] = useState<Reply[]>([]);
    // Blocked (both directions) and muted comments, dropped at render so a block
    // or mute made during the session applies immediately.
    const visibleReplies = filterVisible(replies);
    const [voicePreviewUrl, setVoicePreviewUrl] = useState<string | null>(null);

    const { 
        isRecording, 
        isPaused,      // ⏸️ Added
        duration, 
        startRecording, 
        stopRecording,
        pauseRecording,   // ⏸️ Added
        resumeRecording,  // ⏸️ Added
        audioBlob,
        reset: resetRecorder
    } = useVoiceRecorder(10000);

    const isMock = !event || event.id === 'static';

    useEffect(() => {
        if (audioBlob) {
            const url = URL.createObjectURL(audioBlob);
            setVoicePreviewUrl(url);
        }
    }, [audioBlob]);

    useEffect(() => {
        return () => {
            if (voicePreviewUrl) URL.revokeObjectURL(voicePreviewUrl);
        };
    }, [voicePreviewUrl]);

    useEffect(() => {
        if (isMock || !event?.id) {
            setLikeCount(1247);
            setReplies([
                { id: '1', authorId: 'mock1', author: 'Amara Okafor', username: '@amarao', avatar: '', content: 'This looks absolutely delicious!', time: '2h ago', likes: 24 },
                { id: '2', authorId: 'mock2', author: 'Kwame Mensah', username: '@kwamem', avatar: '', content: 'The spice blend is everything!', time: '4h ago', likes: 18 }
            ]);
            return;
        }

        let unsubscribe: (() => void) | null = null;
        const setupRealtime = async () => {
            const db = await getDbSafe();
            const unsubLikes = onSnapshot(doc(db, 'events', event.id), (docSnap) => {
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    const likesArr = data.likes || [];
                    setLikeCount(likesArr.length);
                    if (userProfile?.id) setLiked(likesArr.includes(userProfile.id));
                }
            });
            const commentsQuery = query(collection(db, `events/${event.id}/comments`), orderBy('createdAt', 'desc'), limit(100));
            const unsubComments = onSnapshot(commentsQuery, (snapshot) => {
                const fetched = snapshot.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        authorId: data.authorId || '',
                        author: data.authorName || 'Unknown',
                        username: data.authorUsername || '@unknown',
                        avatar: data.authorAvatar || '',
                        content: data.content || '',
                        time: data.createdAt ? formatFirestoreTime(data.createdAt) : 'Just now',
                        likes: data.likes?.length || 0,
                        audioUrl: data.audioUrl,
                        duration: data.duration,
                        type: data.type || 'text'
                    };
                });
                // Hidden-content filtering happens at render (see `visibleReplies`)
                // rather than here. This used to hand-roll
                // `!userProfile.blockedUsers.includes(authorId)`, which only covered
                // ONE direction — someone who had blocked the viewer still had their
                // comments shown — and knew nothing about muted words. It was also
                // frozen inside this snapshot closure, so blocking someone mid-session
                // did not take effect until the screen remounted.
                setReplies(fetched);
            });
            unsubscribe = () => { unsubLikes(); unsubComments(); };
        };
        setupRealtime();
        return () => { if (unsubscribe) unsubscribe(); };
    }, [event?.id, userProfile?.id, isMock]);

    const handlePostComment = async () => {
        const hasContent = commentText.trim() || audioBlob;
        if (!hasContent) return;

        const loadingToast = toast.loading('Posting comment...');
        try {
            let audioUrl = '';
            if (audioBlob) {
                audioUrl = await StorageService.uploadVoiceReply(String(userProfile?.id || 'anonymous'), audioBlob);
            }

            if (isMock) {
                setReplies([{
                    id: Date.now().toString(),
                    authorId: userProfile?.id ? String(userProfile.id) : 'you',
                    author: userProfile?.name || 'You',
                    username: userProfile?.username || '@you',
                    avatar: '',
                    content: commentText.trim() || (audioBlob ? '[Voice Message]' : ''),
                    time: 'Now',
                    likes: 0,
                    audioUrl: audioUrl || undefined,
                    duration: audioBlob ? duration : undefined,
                    type: audioBlob ? 'voice' : 'text'
                }, ...replies]);
                toast.dismiss(loadingToast);
                toast.success('Comment posted!');
                resetRecorder();
                setCommentText('');
                return;
            }

            const db = await getDbSafe();
            await addDoc(collection(db, `events/${event.id}/comments`), {
                authorId: userProfile!.id,
                authorName: userProfile!.name,
                authorUsername: userProfile!.username,
                authorAvatar: (userProfile!.portfolio && userProfile!.portfolio[userProfile!.profilePictureIndex || 0]) || userProfile!.image || '',
                content: commentText.trim() || (audioBlob ? '[Voice Message]' : ''),
                audioUrl: audioUrl || null,
                duration: audioBlob ? duration : null,
                type: audioBlob ? 'voice' : 'text',
                createdAt: serverTimestamp(),
                likes: []
            });
            toast.dismiss(loadingToast);
            toast.success('Comment posted!');
            setCommentText('');
            resetRecorder();
        } catch (e) {
            toast.dismiss(loadingToast);
            toast.error('Failed to post comment');
        }
    };

    const handleReportReply = async (reply: Reply) => {
        if (!userProfile?.id) return;
        const reason = window.prompt("Why are you reporting this comment? (Inappropriate, Spam, etc.)");
        if (!reason || reason.trim().length < 4) {
            if (reason !== null) toast.error("A valid reason is required.");
            return;
        }

        try {
            await reportToWatchers(
                String(userProfile.id),
                reply.id,
                'event_comment',
                reason.trim(),
                {
                    eventId: event?.id,
                    authorUsername: reply.username,
                    content: reply.content
                }
            );
            toast.success("Report submitted to Watchers.");
        } catch (e) {
            toast.error('Failed to submit report');
        }
    };

    const handleLike = async () => {
        if (isMock) { setLiked(!liked); setLikeCount(liked ? likeCount - 1 : likeCount + 1); return; }
        if (!userProfile?.id || !event?.id) return;
        const newLike = !liked;
        const delta = newLike ? 1 : -1;
        
        // 🔒 OPTIMISM: Increment like count locally immediately
        adjustMetric(event.id, 'event', 'likesCount', delta);
        setLiked(newLike); 
        setLikeCount(prev => prev + delta);
        
        try {
            const db = await getDbSafe();
            await updateDoc(doc(db, 'events', event.id), {
                likes: newLike ? arrayUnion(String(userProfile.id)) : arrayRemove(String(userProfile.id))
            });
        } catch (e) {
            // Revert on failure
            adjustMetric(event.id, 'event', 'likesCount', -delta);
            setLiked(!newLike); 
            setLikeCount(prev => prev - delta);
            toast.error('Failed to like');
        }
    };

    const bannerUrl = event?.imageUrl || String(foodOfDayImage);
    const title = event?.title || 'Food of the Week';
    const subtitle = event?.description || '';

    return (
        <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
            <ParallaxBackground speed={0.05} />
            <ScrollArea className="h-full">
                <div className="w-full max-w-2xl mx-auto relative z-10 pb-20">
                    <div 
                        className="sticky top-0 z-20 bg-[#FAF8F5]/90 dark:bg-[#0D0D0D]/90 backdrop-blur-sm border-b border-black/10 p-4 flex items-center gap-4"
                        style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 12px))' }}
                    >
                        <Button variant="ghost" size="icon" onClick={onBack} className="rounded-full bg-[#f2deb3] dark:bg-white/10 shrink-0">
                            <ArrowLeft className="w-5 h-5" />
                        </Button>
                        <h1 className="text-lg font-bold text-black dark:text-white truncate" style={HEADING_STYLE}>{event?.title || 'Event Details'}</h1>
                    </div>

                    <div className="p-4 space-y-6">
                        <img src={bannerUrl} alt={title} className="w-full h-auto rounded-2xl shadow-lg border border-black/10" />
                        
                        <div>
                            <h2 className="text-3xl font-bold mb-2 text-black dark:text-white" style={HEADING_STYLE}>{title}</h2>
                            <p className="text-[#666] dark:text-[#E5E5E5] leading-relaxed">{subtitle}</p>
                        </div>

                        {/* Event Sections */}
                        {event?.foodSections && event.foodSections.length > 0 && (
                            <div className="space-y-12 pt-2 pb-6">
                                {event.foodSections.map((section, idx) => (
                                    <EventSection key={idx} section={section} />
                                ))}
                            </div>
                        )}

                        <div className="flex items-center gap-6 p-4 border-y border-black/5">
                            <LikeButton
                                postId={event?.id || 'static'}
                                postType="yap"
                                originalLikeCount={likeCount}
                                canInteract={() => true}
                                isLikedOverride={liked}
                                onToggleOverride={handleLike}
                                displayCountOverride={getOptimisticCount(likeCount, event?.id || 'event', 'event', 'likesCount')}
                            />
                            <div className="flex items-center gap-2 text-gray-500">
                                <MessageCircle className="w-6 h-6" />
                                <span>{replies.length}</span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-white/5 border border-black/10">
                                {voicePreviewUrl ? (
                                    <div className="mb-4">
                                        <VoiceReplyBubble audioUrl={voicePreviewUrl} duration={duration} />
                                        <button onClick={() => { resetRecorder(); setVoicePreviewUrl(null); }} className="text-xs text-red-500 mt-2">Remove</button>
                                    </div>
                                ) : (
                                    <Textarea
                                        placeholder="Add a comment..."
                                        value={commentText}
                                        onChange={e => setCommentText(e.target.value)}
                                        className="bg-transparent border-none resize-none focus-visible:ring-0"
                                        rows={2}
                                        disabled={isRecording}
                                    />
                                )}
                                <div className="flex items-center justify-between mt-2">
                                    <VoiceRecorderUI 
                                        isRecording={isRecording} 
                                        isPaused={isPaused} // ⏸️ Added
                                        duration={duration} 
                                        maxDuration={10000} 
                                        onStart={startRecording} 
                                        onStop={stopRecording} 
                                        onPause={pauseRecording}   // ⏸️ Added
                                        onResume={resumeRecording} // ⏸️ Added
                                        onCancel={resetRecorder} 
                                    />
                                    <Button onClick={handlePostComment} size="sm" className="bg-[#3498DB] hover:bg-[#2980B9] text-white rounded-xl shadow-lg shadow-blue-500/20">
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {visibleReplies.map(reply => (
                                    <div key={reply.id} className="p-4 rounded-2xl bg-white/50 dark:bg-white/5 border border-black/5 relative">
                                        <div className="flex gap-3">
                                            <Avatar className="w-10 h-10 border-2 border-orange-400">
                                                <AvatarImage src={reply.avatar} />
                                                <AvatarFallback>{reply.author[0]}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1">
                                                <div className="font-bold text-sm">{reply.author} <span className="text-xs font-normal text-gray-400 ml-2">{reply.username}</span></div>
                                                <div className="text-[10px] text-gray-400 mb-2">{reply.time}</div>
                                                {reply.content && <p className="text-sm mb-3">{reply.content}</p>}
                                                {reply.audioUrl && <VoiceReplyBubble audioUrl={reply.audioUrl} duration={reply.duration} />}
                                            </div>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end" className="bg-white dark:bg-black border border-black/10">
                                                    <DropdownMenuItem onSelect={() => handleReportReply(reply)} className="text-red-500 cursor-pointer">
                                                        <Megaphone className="w-4 h-4 mr-2" /> Report
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </div>
    );
}
